import { supabase } from './supabase';
import type { SaleFinancialSummary, SaleSharePayload, SalesContribution } from '@klothiq/domain';

export interface SalesFilters {
  from?: string;
  to?: string;
  status?: string;
  payment_status?: string;
  search?: string;
  scope?: 'own' | 'team';
  mode?: 'seller' | 'created';
  person_key?: string;
  include_linked?: boolean;
  include_zero?: boolean;
  offset?: number;
  limit?: number;
  export?: boolean;
}
export function salesError(error: unknown): string {
  const value = error as { code?: string; message?: string };
  if (value?.code === 'PGRST202' || value?.code === '42703')
    return 'The Sales Overview database update is required. Apply the matching migration before using this feature.';
  return value?.message ?? 'Sales details could not be loaded. Please try again.';
}
export async function salesRpc<T>(name: string, args: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.rpc(name, args);
  if (error) throw new Error(salesError(error));
  return data as T;
}
export const fetchSaleShare = (id: string) =>
  salesRpc<SaleSharePayload>('get_sale_whatsapp_payload', { p_sale_id: id });
export const fetchContribution = (filters: SalesFilters) =>
  salesRpc<SalesContribution>('get_sales_contribution', { p_filters: filters });
export async function fetchSalesPage<T>(
  filters: SalesFilters,
): Promise<{ rows: T[]; total: number }> {
  return salesRpc('list_sales_overview', { p_filters: filters });
}
export async function fetchAllSales<T extends { id: string }>(filters: SalesFilters): Promise<T[]> {
  const rows: T[] = [];
  const seen = new Set<string>();
  let offset = 0;
  let expected: number | undefined;
  while (true) {
    const page = await fetchSalesPage<T>({ ...filters, offset, limit: 1000 });
    if (expected !== undefined && page.total !== expected)
      throw new Error('The sales list changed. Refresh before loading or exporting again.');
    expected = page.total;
    for (const row of page.rows) {
      if (seen.has(row.id)) throw new Error('Sales changed while paging. Refresh and try again.');
      seen.add(row.id);
      rows.push(row);
    }
    offset += page.rows.length;
    if (offset >= page.total) return rows;
    if (!page.rows.length) throw new Error('Sales changed while loading. Refresh and try again.');
  }
}
export function financialSummary(value: {
  financial_summary?: SaleFinancialSummary;
  sale_status?: string;
  return_status?: string | null;
  paid_amount?: unknown;
  balance_due?: unknown;
}): SaleFinancialSummary {
  if (value.financial_summary) return value.financial_summary;
  const review =
    ['returned', 'partially_returned'].includes(value.sale_status ?? '') ||
    value.return_status === 'completed';
  return {
    collected: Number(value.paid_amount ?? 0),
    refunded: 0,
    net_collected: Number(value.paid_amount ?? 0),
    amount_due:
      review || ['cancelled', 'draft'].includes(value.sale_status ?? '')
        ? null
        : Math.max(0, Number(value.balance_due ?? 0)),
    return_review: review,
  };
}
